/**
 * pages/Activity.tsx — the full activity feed.
 *
 * Design contract (user-specified):
 *
 *   * Desktop: table with sticky header, sortable Date column,
 *     right-aligned tabular amounts.
 *   * Mobile:  cell list (avatar + title + meta on the left, amount
 *     + status on the right). NEVER a horizontally-scrolling table.
 *   * Four statuses visually distinct. Reversed is NOT failed —
 *     see the reversed-explainer banner + StatusChip colour.
 *   * Empty and filtered-to-zero are DIFFERENT states.
 *   * Cursor pagination, never offset.
 *
 * Data source:
 *   GET /v1/activity?cursor=<opaque>&limit=20
 *   Returns { data: ActivityItem[], next_cursor: string | null }
 *
 * Backend today supports cursor + limit only. Direction / status /
 * search filtering is applied client-side over the loaded pages —
 * "Load more" fetches additional pages and the filter re-applies.
 * When backend filters land, swap the client-side filter for a
 * server-side param and drop the local re-filter. The UX contract
 * stays identical.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Field,
  Money,
  Skeleton,
  StatusChip,
  Table,
  Tabs,
  type TableColumn,
} from "../components";
import { fetchActivity, moneyMinor } from "../api/wallet";
import type { ActivityItem } from "../api/wallet";
import { ApiClientError } from "../api/client";
import { AppShell } from "./AppShell";
import styles from "./Activity.module.css";

// ---------------------------------------------------------------------------
// Filter model.
// ---------------------------------------------------------------------------

type DirectionFilter = "all" | "in" | "out";
type StatusFilter = "all" | "pending" | "completed" | "failed" | "reversed";
type DateOrder = "desc" | "asc";

interface Filters {
  direction: DirectionFilter;
  status: StatusFilter;
  q: string;
}

const DIRECTION_TABS = [
  { value: "all", label: "All" },
  { value: "in", label: "Incoming" },
  { value: "out", label: "Outgoing" },
] as const;

const STATUS_CHIPS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All statuses" },
  { key: "pending", label: "Pending" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
  { key: "reversed", label: "Reversed" },
];

// ---------------------------------------------------------------------------
// Activity page.
// ---------------------------------------------------------------------------

export function Activity() {
  const [rows, setRows] = useState<ActivityItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    direction: "all",
    status: "all",
    q: "",
  });
  const [dateOrder, setDateOrder] = useState<DateOrder>("desc");
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // ---- Fetch --------------------------------------------------------------
  const loadPage = useCallback(async (cursor: string | null) => {
    try {
      const isFirst = cursor === null;
      if (isFirst) setInitialLoading(true);
      else setPageLoading(true);
      const page = await fetchActivity(cursor, 20);
      setRows((prev) => (isFirst ? page.data : [...prev, ...page.data]));
      setNextCursor(page.next_cursor);
      setLoadError(null);
    } catch (e) {
      const msg =
        e instanceof ApiClientError ? e.message : "Couldn’t load activity";
      setLoadError(msg);
    } finally {
      setInitialLoading(false);
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPage(null);
  }, [loadPage]);

  // ---- Client-side filter + sort -----------------------------------------
  const visibleRows = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (filters.direction !== "all") {
        const wantDir = filters.direction === "in" ? "in" : "out";
        if (r.direction !== wantDir) return false;
      }
      if (filters.status !== "all" && r.status !== filters.status) return false;
      if (q) {
        const haystack = [r.title, r.subtitle, r.kind]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    if (dateOrder === "asc") {
      out = [...out].sort((a, b) =>
        a.occurred_at < b.occurred_at ? -1 : a.occurred_at > b.occurred_at ? 1 : 0,
      );
    }
    return out;
  }, [rows, filters, dateOrder]);

  const anyReversedVisible = useMemo(
    () => visibleRows.some((r) => r.status === "reversed"),
    [visibleRows],
  );

  // ---- Table columns (desktop) -------------------------------------------
  const columns = useMemo<TableColumn<ActivityItem>[]>(
    () => [
      {
        key: "when",
        label: "Date",
        width: 120,
        render: (r) => (
          <span className={styles.dateCell}>
            {formatDate(r.occurred_at, true)}
          </span>
        ),
      },
      {
        key: "cp",
        label: "Counterparty",
        render: (r) => (
          <span className={styles.cpCell}>
            <Avatar
              initials={r.initials || (r.title || "?").slice(0, 2).toUpperCase()}
              size={28}
            />
            <span className={styles.cpText}>
              <span className={styles.cpTitle}>{r.title}</span>
              {r.subtitle ? (
                <span className={styles.cpSubtitle}>{r.subtitle}</span>
              ) : null}
            </span>
          </span>
        ),
      },
      {
        key: "kind",
        label: "Kind",
        width: 120,
        render: (r) => (
          <span className={styles.kindCell}>{kindLabel(r.kind, r.direction)}</span>
        ),
      },
      {
        key: "amount",
        label: "Amount",
        align: "right",
        width: 180,
        render: (r) => {
          const minor = moneyMinor(r.amount);
          // Reversed / failed → muted intent. Never red on the amount
          // itself — the chip carries that signal.
          const muted = r.status === "failed" || r.status === "reversed";
          return (
            <Money
              value={minor}
              asset={r.amount.asset}
              size="amount"
              intent={muted ? "muted" : minor > 0n ? "credit" : "default"}
            />
          );
        },
      },
      {
        key: "status",
        label: "Status",
        width: 130,
        render: (r) => <StatusChip status={r.status} />,
      },
    ],
    [],
  );

  // ---- Header sort toggle ------------------------------------------------
  // We flip the currently-loaded page. Backend returns strict desc
  // (uuidv7 order); flipping to asc doesn't fetch older items first
  // — surface that with the hint below the sortable header.
  const toggleSort = useCallback(() => {
    setDateOrder((d) => (d === "desc" ? "asc" : "desc"));
  }, []);

  const filtersActive =
    filters.direction !== "all" || filters.status !== "all" || filters.q !== "";

  // ---- Empty state discrimination ---------------------------------------
  const emptyKind: "never" | "filtered" | null = (() => {
    if (initialLoading) return null;
    if (rows.length === 0) return "never";
    if (visibleRows.length === 0) return "filtered";
    return null;
  })();

  const clearFilters = useCallback(() => {
    setFilters({ direction: "all", status: "all", q: "" });
  }, []);

  // ---- Render ------------------------------------------------------------
  return (
    <AppShell current="activity">
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Activity</p>
          <h1 className={styles.pageTitle}>Everything that touched your balance</h1>
        </div>
      </header>

      {/* Filter bar — direction tabs + status chips + search */}
      <section className={styles.filterBar}>
        <Tabs
          options={DIRECTION_TABS}
          value={filters.direction}
          onChange={(v) => setFilters((f) => ({ ...f, direction: v }))}
          ariaLabel="Filter by direction"
        />

        <div className={styles.filterRight}>
          <div
            className={styles.statusChips}
            role="group"
            aria-label="Filter by status"
          >
            {STATUS_CHIPS.map(({ key, label }) => {
              const active = filters.status === key;
              return (
                <button
                  key={key}
                  className={`${styles.statusChip} ${active ? styles.statusChipActive : ""}`}
                  onClick={() => setFilters((f) => ({ ...f, status: key }))}
                  aria-pressed={active}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className={styles.searchWrap}>
            <Field
              label=""
              value={filters.q}
              onChange={(v) => setFilters((f) => ({ ...f, q: v }))}
              placeholder="Search counterparty or memo"
              aria-label="Search activity"
              type="search"
            />
          </div>
        </div>
      </section>

      {/* Reversed explainer — surfaces the plain-language rule when
          at least one reversed row is visible. */}
      {anyReversedVisible && (
        <Card className={styles.reversedBanner} padding={0}>
          <div className={styles.reversedBannerInner}>
            <span className={styles.reversedGlyph} aria-hidden>
              ↩
            </span>
            <div>
              <p className={styles.reversedTitle}>Reversed transactions</p>
              <p className={styles.reversedBody}>
                Money that arrived and was later returned to the sender —
                or that you sent and had returned to you. It is <strong>not</strong>{" "}
                the same as a failed transaction, where no money moved at all.
              </p>
            </div>
          </div>
        </Card>
      )}

      {loadError && (
        <Card className={styles.errorCard}>
          <span className={styles.errorLine}>
            Couldn’t load activity — {loadError}.
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void loadPage(null)}
          >
            Retry
          </Button>
        </Card>
      )}

      {/* Loading skeleton */}
      {initialLoading && (
        <Card padding={0}>
          <div className={styles.tableSkeleton}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="row" />
            ))}
          </div>
        </Card>
      )}

      {/* Empty states */}
      {emptyKind === "never" && (
        <Card>
          <EmptyState
            title="No activity yet"
            body="Your transactions will appear here as they clear. Money you receive shows first; money you send shows after step-up on your phone."
          />
        </Card>
      )}
      {emptyKind === "filtered" && (
        <Card>
          <EmptyState
            title="No matching activity"
            body="Nothing matched the filters you have applied. Try widening the search."
            action={
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        </Card>
      )}

      {/* Table (desktop) + list (mobile) */}
      {!initialLoading && visibleRows.length > 0 && (
        <>
          <div className={styles.desktopOnly}>
            <Card padding={0} className={styles.tableCard}>
              <div className={styles.tableWrap}>
                <div className={styles.sortBar}>
                  <button
                    className={styles.sortButton}
                    onClick={toggleSort}
                    aria-label={`Sort by date ${dateOrder === "desc" ? "ascending" : "descending"}`}
                  >
                    Date · {dateOrder === "desc" ? "Newest first" : "Oldest first"}
                    <span className={styles.sortArrow} aria-hidden>
                      {dateOrder === "desc" ? "↓" : "↑"}
                    </span>
                  </button>
                  {dateOrder === "asc" && (
                    <span className={styles.sortHint}>
                      Sorted from the {rows.length} loaded rows. Load more to include older transactions.
                    </span>
                  )}
                </div>
                <Table
                  columns={columns}
                  rows={visibleRows}
                  rowKey={(r) => r.id}
                  stickyHeader
                />
              </div>
            </Card>
          </div>
          <div className={styles.mobileList}>
            <ActivityList rows={visibleRows} />
          </div>
        </>
      )}

      {/* Cursor pagination footer */}
      {!initialLoading && rows.length > 0 && (
        <div className={styles.paginationFoot} ref={loadMoreRef}>
          {nextCursor ? (
            <Button
              variant="secondary"
              onClick={() => void loadPage(nextCursor)}
              loading={pageLoading}
            >
              {pageLoading ? "Loading…" : "Load older activity"}
            </Button>
          ) : (
            <p className={styles.endMessage}>
              End of activity · {rows.length} {rows.length === 1 ? "row" : "rows"} loaded
              {filtersActive
                ? ` · ${visibleRows.length} matching current filters`
                : ""}
              .
            </p>
          )}
        </div>
      )}
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Mobile activity cell list.
// ---------------------------------------------------------------------------

function ActivityList({ rows }: { rows: ActivityItem[] }) {
  return (
    <Card padding={0}>
      {rows.map((r) => (
        <ActivityCell key={r.id} row={r} />
      ))}
    </Card>
  );
}

function ActivityCell({ row }: { row: ActivityItem }) {
  const minor = moneyMinor(row.amount);
  const muted = row.status === "failed" || row.status === "reversed";
  return (
    <div className={styles.cell}>
      <Avatar
        initials={row.initials || (row.title || "?").slice(0, 2).toUpperCase()}
        size={32}
      />
      <div className={styles.cellMain}>
        <div className={styles.cellTitle}>{row.title}</div>
        <div className={styles.cellMeta}>
          <span>{formatDate(row.occurred_at, true)}</span>
          {row.subtitle ? (
            <>
              <span className={styles.cellMetaDot} />
              <span>{row.subtitle}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className={styles.cellRight}>
        <Money
          value={minor}
          asset={row.amount.asset}
          size="amount"
          intent={muted ? "muted" : minor > 0n ? "credit" : "default"}
        />
        <StatusChip status={row.status} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers.
// ---------------------------------------------------------------------------

function formatDate(iso: string, withYear = false): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
    if (withYear && d.getUTCFullYear() !== now.getUTCFullYear()) {
      opts.year = "numeric";
    }
    return d.toLocaleDateString(undefined, opts);
  } catch {
    return iso.slice(0, 10);
  }
}

function kindLabel(kind: string, direction: "in" | "out"): string {
  switch (kind) {
    case "transfer":
      return direction === "in" ? "Received" : "Sent";
    case "topup":
      return "Top-up";
    case "qr_pay":
      return direction === "in" ? "QR received" : "QR paid";
    case "request":
      return direction === "in" ? "Request paid" : "Request sent";
    default:
      // Sentence-case whatever came back.
      return kind
        .replace(/[_-]+/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase());
  }
}
